'use client'

import { MediaItem } from '@/types/medias'
import { X } from 'lucide-react'
import { useEffect } from 'react'
import { createPortal } from 'react-dom'

type VideoModalProps = {
	isOpen: boolean
	videoItem: MediaItem | null
	onClose: () => void
}

export default function VideoModal({
	isOpen,
	videoItem,
	onClose,
}: VideoModalProps) {
	// Handle escape key to close modal
	useEffect(() => {
		const handleEscape = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				onClose()
			}
		}

		if (isOpen) {
			document.addEventListener('keydown', handleEscape)
			// Prevent body scroll when modal is open
			document.body.style.overflow = 'hidden'
		}

		return () => {
			document.removeEventListener('keydown', handleEscape)
			document.body.style.overflow = 'unset'
		}
	}, [isOpen, onClose])

	// Handle click outside modal to close
	const handleBackdropClick = (event: React.MouseEvent) => {
		if (event.target === event.currentTarget) {
			onClose()
		}
	}

	if (!isOpen || !videoItem) {
		return null
	}

	const modalContent = (
		<div
			className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[100] p-4"
			onClick={handleBackdropClick}
		>
			<div className="relative w-full max-w-4xl max-h-[90vh] bg-white rounded-3xl p-2 pt-4 lg:p-8 lg:pt-6">
				<div className="flex justify-between items-start px-2 lg:px-0 gap-8">
					{/* Title */}
					<h4 className="text-md lg:text-lg font-bold mb-2 lg:mb-6">
						{videoItem.title}
					</h4>
					{/* Close button */}
					<button
						onClick={onClose}
						className="text-2xl font-bold bg-gray-100 rounded-full p-2 hover:bg-gray-200 z-10 transition-colors"
						aria-label="Close video modal"
					>
						<X strokeWidth={2} className="w-4 h-4" />
					</button>
				</div>

				{/* Video container */}
				<div className="relative w-full aspect-video rounded-2xl overflow-hidden">
					<iframe
						className="w-full h-full rounded-2xl"
						src={`${videoItem.videoUrl?.replace('/watch?v=', '/embed/')}?autoplay=1&playsinline=1&modestbranding=1&rel=0`}
						title={videoItem.title || 'Video'}
						allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
						allowFullScreen
					/>
				</div>
			</div>
		</div>
	)

	// Use portal to render modal at document body level
	return createPortal(modalContent, document.body)
}
